// AcademicProjectHODManager.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import UploadIcon from "@mui/icons-material/Upload";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InputAdornment from "@mui/material/InputAdornment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import HistoryIcon from "@mui/icons-material/History";
import { API_URL } from "./config";

const AcademicProjectHODManager = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    borderRadius: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const filterSectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const sectionTitleSx = {
    fontWeight: 900,
    color: BLUE,
    mb: 1,
  };

  const token = localStorage.getItem("auth") || localStorage.getItem("token");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const [loginCampusCode, setLoginCampusCode] = useState("");
  const [loginUsername, setLoginUsername] = useState("");

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [subCampusOptions, setSubCampusOptions] = useState([]);
  const [degreeOptions, setDegreeOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [classTeacherOptions, setClassTeacherOptions] = useState([]);
  const [regulationOptions, setRegulationOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [completedTopics, setCompletedTopics] = useState([]);
  const [generatingProject, setGeneratingProject] = useState(false);

  // Multi-project AI generation
  const [projectVariationCount, setProjectVariationCount] = useState(1);
  const [generatedProjects, setGeneratedProjects] = useState([]);
  const [savingGeneratedProjects, setSavingGeneratedProjects] = useState(false);

  // Existing assignment groups for the currently selected academic scope.
  // Users can select one from the dropdown or type a new group code.
  const [assignmentGroupOptions, setAssignmentGroupOptions] = useState([]);

  const [prompts, setPrompts] = useState([]);
  const [projects, setProjects] = useState([]);

  const bulkProjectFileRef = useRef(null);

  const downloadProjectSampleExcel = () => {
    const headers = [
      "AcademicYear",
      "SubCampusCode",
      "DegreeCode",
      "BranchCode",
      "Regulation",
      "Semester",
      "Sections",
      "SubjectCodes",
      "AssignmentGroupCode",
      "ProjectNumber",
      "ProjectTitle",
      "Domain",
      "ProjectDescription",
      "ProblemStatement",
      "ExpectedOutcome",
      "PromptUsed",
      "TechStack",
      "DifficultyLevel",
      "StartDate",
      "EndDate",
      "MaxStudentsAllowed",
      "Status",
    ];
  
    const sampleRows = [
      {
        AcademicYear: "2025-26",
        SubCampusCode: "ENG",
        DegreeCode: "BTECH",
        BranchCode: "CSE",
        Regulation: "R23",
        Semester: "3",
        Sections: "A,B",
        SubjectCodes: "CS301,CS302",
        AssignmentGroupCode: "SubjectCode_ProjectNumber",
        ProjectNumber: 1,
        ProjectTitle: "Smart Irrigation Monitoring System",
        Domain: "Agriculture IoT",
        ProjectDescription: "Build an IoT-based irrigation monitoring system.",
        ProblemStatement: "Farmers need real-time soil moisture and water usage visibility.",
        ExpectedOutcome: "Students should build dashboard, sensor simulation, and alert system.",
        PromptUsed: "Generate agriculture IoT projects for CSE students.",
        TechStack: "React, Node.js, MySQL, IoT Sensors",
        DifficultyLevel: "Medium",
        StartDate: "2026-07-01",
        EndDate: "2026-08-15",
        MaxStudentsAllowed: 5,
        Status: "DRAFT",
      },
    ];
  
    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(18, h.length + 2) }));
  
    const instructions = [
      ["Field", "Instruction"],
      ["Sheet Name", "Keep sheet name exactly: AcademicProjects"],
      ["Sections", "Comma separated. Example: A,B,C"],
      ["SubjectCodes", "Comma separated. Example: CS301,CS302"],
      ["Status", "Use only DRAFT or PUBLISHED"],
      ["StartDate / EndDate", "Use YYYY-MM-DD"],
      ["AssignmentGroupCode", "Required grouping code. All projects in one distribution round use the same value, for example SubjectCode_ProjectNumber."],
      ["ProjectNumber", "Optional display number within the assignment group, for example 1, 2, 3."],
      ["MaxStudentsAllowed", "Numeric only. Example: 5"],
      ["Important", "Scope must exist in classes table."],
    ];
  
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 24 }, { wch: 90 }];
  
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AcademicProjects");
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");
  
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  
    saveAs(blob, `AcademicProjects_Sample_${loginCampusCode || "Campus"}.xlsx`);
  };

  const [projectPage, setProjectPage] = useState(1);
  const PROJECTS_PER_PAGE = 10;

  const emptyProjectForm = {
    ProjectID: "",
    SubCampusCode: "",
    AcademicYear: "",
    DegreeCode: "",
    BranchCode: "",
    Regulation: "",
    Semester: "",
    Sections: [],
    Subjects: [],

    AssignmentGroupCode: "",
    ProjectNumber: "",
    ProjectTitle: "",
    Domain: "",
    ProjectDescription: "",
    ProblemStatement: "",
    ExpectedOutcome: "",
    PromptUsed: "",
  
    TechStack: "",
    DifficultyLevel: "Medium",
    StartDate: "",
    EndDate: "",
    MaxStudentsAllowed: 5,
    Status: "DRAFT",
  };

  const emptyPromptForm = {
    PromptID: "",
    PromptTitle: "",
    PromptPurpose: "PROJECT_IDEATION",
    PromptText: "",
  };

  const toDateInputValue = (value) => {
    if (!value) return "";
    return String(value).slice(0, 10);
  };

  const normalizeAssignmentGroupCode = (value) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const [projectForm, setProjectForm] = useState(emptyProjectForm);
  const [promptForm, setPromptForm] = useState(emptyPromptForm);

  const selectedAssignmentGroupInfo = useMemo(() => {
    const code = normalizeAssignmentGroupCode(projectForm.AssignmentGroupCode);
    if (!code) return null;

    return (
      assignmentGroupOptions.find(
        (item) => item.AssignmentGroupCode === code
      ) || null
    );
  }, [assignmentGroupOptions, projectForm.AssignmentGroupCode]);

  const previewNextProjectNumber =
    Number(selectedAssignmentGroupInfo?.NextProjectNumber || 1) +
    generatedProjects.length;

  const [filters, setFilters] = useState({
    SubCampusCode: "",
    AcademicYear: "",
    DegreeCode: "",
    BranchCode: "",
    Regulation: "",
    Semester: "",
    Section: "",
    AssignmentGroupCode: "",
    Status: "",
  });

  const [sectionTeacherOptions, setSectionTeacherOptions] = useState([]);

  const decodeToken = () => {
    if (!token) return;

    try {
      const payload = JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
      );

      setLoginCampusCode(payload?.campusCode || payload?.CampusCode || "");
      setLoginUsername(payload?.username || payload?.Username || payload?.userName || "");
    } catch {
      setError("Failed to decode auth token");
    }
  };

  useEffect(() => {
    decodeToken();
  }, [token]);

  const fetchAcademicYears = async () => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/academicyears`, {
      headers: authHeaders,
    });

    const rows = Array.isArray(res.data) ? res.data : [];
    setAcademicYearOptions(
      rows.map((x) => x.AcademicYearLabel || x.AcademicYear || "").filter(Boolean)
    );
  };

  const fetchSubCampuses = async () => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/subcampuses`, {
      headers: authHeaders,
    });

    setSubCampusOptions(res.data || []);
  };

  const fetchDegrees = async (SubCampusCode) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/degrees`, {
      headers: authHeaders,
      params: { SubCampusCode },
    });

    setDegreeOptions(res.data || []);
  };

  const fetchBranches = async (SubCampusCode, DegreeCode) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/branches`, {
      headers: authHeaders,
      params: { SubCampusCode, DegreeCode },
    });

    setBranchOptions(res.data || []);
  };

  const fetchRegulations = async (SubCampusCode, DegreeCode, BranchCode) => {
  const res = await axios.get(`${API_URL}/api/academic-projects/regulations`, {
      headers: authHeaders,
      params: {
        CampusCode: loginCampusCode,
        SubCampusCode,
        DegreeCode,
        BranchCode,
      },
    });
  
    setRegulationOptions(res.data || []);
  };

  const fetchSemesters = async (SubCampusCode, DegreeCode, BranchCode) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/semesters`, {
      headers: authHeaders,
      params: { SubCampusCode, DegreeCode, BranchCode },
    });

    setSemesterOptions(res.data || []);
  };
{/* NOTE: Sections are now fetched from a different endpoint that also requires regulation as param, since sections can differ based on regulation changes. */}
{/*
  const fetchSections = async (SubCampusCode, DegreeCode, BranchCode, Semester) => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/sections`, {
      headers: authHeaders,
      params: { SubCampusCode, DegreeCode, BranchCode, Semester },
    });

    setSectionOptions(res.data || []);
  };
*/}
  const fetchProjectSections = async () => {
  const res = await axios.get(`${API_URL}/api/academic-projects/sections`, {
      headers: authHeaders,
      params: {
        CampusCode: loginCampusCode,
        AcademicYear: projectForm.AcademicYear,
        SubCampusCode: projectForm.SubCampusCode,
        DegreeCode: projectForm.DegreeCode,
        BranchCode: projectForm.BranchCode,
        Regulation: projectForm.Regulation,
        Semester: projectForm.Semester,
      },
    });
  
    setSectionOptions(res.data || []);
  };

  const fetchProjectSubjects = async () => {
    const res = await axios.get(`${API_URL}/api/academic-projects/subjects`, {
      headers: authHeaders,
      params: {
        CampusCode: loginCampusCode,
        AcademicYear: projectForm.AcademicYear,
        SubCampusCode: projectForm.SubCampusCode,
        DegreeCode: projectForm.DegreeCode,
        BranchCode: projectForm.BranchCode,
        Regulation: projectForm.Regulation,
        Semester: projectForm.Semester,
        Sections: projectForm.Sections.join(","),
      },
    });
  
    setSubjectOptions(res.data || []);
  };  

  const fetchCompletedTopics = async (subjects = []) => {
    const classIds = subjects
      .map((x) => x.ClassID)
      .filter(Boolean);
  
    if (!classIds.length) {
      setCompletedTopics([]);
      return;
    }
  
    const res = await axios.post(
      `${API_URL}/api/academic-projects/completed-topics`,
      { ClassIDs: classIds },
      { headers: authHeaders }
    );
  
    setCompletedTopics(res.data || []);
  };

  const fetchPrompts = async () => {
    const res = await axios.get(`${API_URL}/api/academic-projects/academic-project-prompts`, {
      headers: authHeaders,
      params: {
        CampusCode: loginCampusCode,
        SubCampusCode: projectForm.SubCampusCode || undefined,
      },
    });

    setPrompts(res.data || []);
  };

  const fetchProjects = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`${API_URL}/api/academic-projects`, {
        headers: authHeaders,
        params: {
          CampusCode: loginCampusCode,
          ...filters,
        },
      });

      setProjects(res.data || []);
      setProjectPage(1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load academic projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignmentGroups = async () => {
    if (
      !loginCampusCode ||
      !projectForm.SubCampusCode ||
      !projectForm.AcademicYear ||
      !projectForm.DegreeCode ||
      !projectForm.BranchCode ||
      !projectForm.Regulation ||
      !projectForm.Semester
    ) {
      setAssignmentGroupOptions([]);
      return;
    }

    try {
      const res = await axios.get(
        `${API_URL}/api/academic-projects/assignment-groups`,
        {
          headers: authHeaders,
          params: {
            SubCampusCode: projectForm.SubCampusCode,
            AcademicYear: projectForm.AcademicYear,
            DegreeCode: projectForm.DegreeCode,
            BranchCode: projectForm.BranchCode,
            Regulation: projectForm.Regulation,
            Semester: projectForm.Semester,
          },
        }
      );

      setAssignmentGroupOptions(
        Array.isArray(res.data) ? res.data : []
      );
    } catch (err) {
      setAssignmentGroupOptions([]);
      setError(
        err.response?.data?.message ||
          "Failed to load assignment groups"
      );
    }
  };

  const fetchSectionTeachers = async () => {
    if (
      !projectForm.SubCampusCode ||
      !projectForm.DegreeCode ||
      !projectForm.BranchCode ||
      !projectForm.Regulation ||
      !projectForm.Semester ||
      !projectForm.Sections.length
    ) {
      setSectionTeacherOptions([]);
      return;
    }
  
    const res = await axios.post(
      `${API_URL}/api/academic-projects/section-class-teachers`,
      {
        CampusCode: loginCampusCode,
        SubCampusCode: projectForm.SubCampusCode,
        DegreeCode: projectForm.DegreeCode,
        BranchCode: projectForm.BranchCode,
        Regulation: projectForm.Regulation,
        Semester: projectForm.Semester,
        Sections: projectForm.Sections,
      },
      { headers: authHeaders }
    );
  
    setSectionTeacherOptions(res.data || []);
  };

  useEffect(() => {
    fetchSectionTeachers().catch(() =>
      setError("Failed to load class teachers for selected sections")
    );
  }, [
    projectForm.SubCampusCode,
    projectForm.DegreeCode,
    projectForm.BranchCode,
    projectForm.Regulation,
    projectForm.Semester,
    projectForm.Sections,
  ]);

  useEffect(() => {
    if (!token || !loginCampusCode) return;

    fetchAcademicYears().catch(() => setError("Failed to load academic years"));
    fetchSubCampuses().catch(() => setError("Failed to load subcampuses"));
    fetchProjects();
  }, [token, loginCampusCode]);

  useEffect(() => {
    if (!loginCampusCode) return;
    fetchPrompts().catch(() => setError("Failed to load prompts"));
  }, [loginCampusCode, projectForm.SubCampusCode]);

  useEffect(() => {
    if (!projectForm.SubCampusCode) {
      setDegreeOptions([]);
      return;
    }

    fetchDegrees(projectForm.SubCampusCode).catch(() => setError("Failed to load degrees"));
  }, [projectForm.SubCampusCode]);

  useEffect(() => {
    if (!projectForm.SubCampusCode || !projectForm.DegreeCode) {
      setBranchOptions([]);
      return;
    }

    fetchBranches(projectForm.SubCampusCode, projectForm.DegreeCode).catch(() =>
      setError("Failed to load branches")
    );
  }, [projectForm.SubCampusCode, projectForm.DegreeCode]);

  useEffect(() => {
    if (!projectForm.SubCampusCode || !projectForm.DegreeCode || !projectForm.BranchCode) {
      setRegulationOptions([]);
      return;
    }
  
    fetchRegulations(
      projectForm.SubCampusCode,
      projectForm.DegreeCode,
      projectForm.BranchCode
    ).catch(() => setError("Failed to load regulations"));
  }, [projectForm.SubCampusCode, projectForm.DegreeCode, projectForm.BranchCode]);

  useEffect(() => {
    if (!projectForm.SubCampusCode || !projectForm.DegreeCode || !projectForm.BranchCode) {
      setSemesterOptions([]);
      return;
    }

    fetchSemesters(
      projectForm.SubCampusCode,
      projectForm.DegreeCode,
      projectForm.BranchCode
    ).catch(() => setError("Failed to load semesters"));
  }, [projectForm.SubCampusCode, projectForm.DegreeCode, projectForm.BranchCode]);

  useEffect(() => {
    if (
      !projectForm.SubCampusCode ||
      !projectForm.DegreeCode ||
      !projectForm.BranchCode ||
      !projectForm.Regulation ||
      !projectForm.Semester
    ) {
      setSectionOptions([]);
      setSubjectOptions([]);
      return;
    }
  
    fetchProjectSections().catch(() => setError("Failed to load sections"));
  }, [
    projectForm.AcademicYear,
    projectForm.SubCampusCode,
    projectForm.DegreeCode,
    projectForm.BranchCode,
    projectForm.Regulation,
    projectForm.Semester,
  ]);

  useEffect(() => {
    if (
      !projectForm.AcademicYear ||
      !projectForm.SubCampusCode ||
      !projectForm.DegreeCode ||
      !projectForm.BranchCode ||
      !projectForm.Regulation ||
      !projectForm.Semester ||
      !projectForm.Sections.length
    ) {
      setSubjectOptions([]);
      setProjectForm((prev) =>
        prev.Subjects.length ? { ...prev, Subjects: [] } : prev
      );
      return;
    }

    fetchProjectSubjects().catch(() => setError("Failed to load subjects"));
  }, [
    projectForm.AcademicYear,
    projectForm.SubCampusCode,
    projectForm.DegreeCode,
    projectForm.BranchCode,
    projectForm.Regulation,
    projectForm.Semester,
    projectForm.Sections,
  ]);

  useEffect(() => {
    fetchAssignmentGroups();
  }, [
    loginCampusCode,
    projectForm.SubCampusCode,
    projectForm.AcademicYear,
    projectForm.DegreeCode,
    projectForm.BranchCode,
    projectForm.Regulation,
    projectForm.Semester,
  ]);

  // Keep ProjectNumber populated for new projects whenever an assignment group is selected.
  // Existing groups use the backend-provided NextProjectNumber; a brand-new group starts at 1.
  useEffect(() => {
    if (projectForm.ProjectID) return;

    const normalizedGroup = normalizeAssignmentGroupCode(
      projectForm.AssignmentGroupCode
    );

    if (!normalizedGroup || projectForm.ProjectNumber !== "") return;

    const existingGroup = assignmentGroupOptions.find(
      (item) =>
        normalizeAssignmentGroupCode(item.AssignmentGroupCode) === normalizedGroup
    );

    setProjectForm((prev) => ({
      ...prev,
      ProjectNumber: Number(existingGroup?.NextProjectNumber || 1),
    }));
  }, [
    assignmentGroupOptions,
    projectForm.AssignmentGroupCode,
    projectForm.ProjectID,
    projectForm.ProjectNumber,
  ]);

  useEffect(() => {
    if (!projectForm.Subjects || projectForm.Subjects.length === 0) {
      setCompletedTopics([]);
      return;
    }
  
    fetchCompletedTopics(projectForm.Subjects).catch(() =>
      setError("Failed to load completed topics")
    );
  }, [projectForm.Subjects]);  

  const resetProjectForm = () => {
    setProjectForm(emptyProjectForm);
    setGeneratedProjects([]);
    setProjectVariationCount(1);
    setAssignmentGroupOptions([]);
  };

  const resetPromptForm = () => {
    setPromptForm(emptyPromptForm);
  };

  const handleProjectChange = (e) => {
    const { name, value } = e.target;

    setProjectForm((prev) => {
      if (name === "AcademicYear") {
        setGeneratedProjects([]);
        return {
          ...prev,
          AcademicYear: value,
          AssignmentGroupCode: "",
          ProjectNumber: "",
        };
      }

      if (name === "SubCampusCode") {
        setCompletedTopics([]);
        setGeneratedProjects([]);
        return {
          ...prev,
          SubCampusCode: value,
          DegreeCode: "",
          BranchCode: "",
          Regulation: "",
          Semester: "",
          Sections: [],
          Subjects: [],
          AssignmentGroupCode: "",
          ProjectNumber: "",
        };
      }

      if (name === "DegreeCode") {
        setCompletedTopics([]);
        setGeneratedProjects([]);
        return {
          ...prev,
          DegreeCode: value,
          BranchCode: "",
          Regulation: "",
          Semester: "",
          Sections: [],
          Subjects: [],
          AssignmentGroupCode: "",
          ProjectNumber: "",
        };
      }

      if (name === "BranchCode") {
        setCompletedTopics([]);
        setGeneratedProjects([]);
        return {
          ...prev,
          BranchCode: value,
          Regulation: "",
          Semester: "",
          Sections: [],
          Subjects: [],
          AssignmentGroupCode: "",
          ProjectNumber: "",
        };
      }

      if (name === "Regulation") {
        setCompletedTopics([]);
        setGeneratedProjects([]);
        return {
          ...prev,
          Regulation: value,
          Semester: "",
          Sections: [],
          Subjects: [],
          AssignmentGroupCode: "",
          ProjectNumber: "",
        };
      }

      if (name === "Semester") {
        setCompletedTopics([]);
        setGeneratedProjects([]);
        return {
          ...prev,
          Semester: value,
          Sections: [],
          Subjects: [],
          AssignmentGroupCode: "",
          ProjectNumber: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handlePromptChange = (e) => {
    const { name, value } = e.target;
    setPromptForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUsePrompt = (prompt) => {
    setProjectForm((prev) => ({
      ...prev,
      PromptID: prompt.PromptID,
      PromptUsed: prompt.PromptText,
    }));
  
    setSuccess("Prompt copied into project form");
    setTab(0);
  };

  const handleCopyPrompt = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      setSuccess("Prompt copied");
    } catch {
      setError("Failed to copy prompt");
    }
  };

  const handleSavePrompt = async () => {
    if (!promptForm.PromptTitle || !promptForm.PromptText) {
      setError("Prompt title and prompt text are required");
      return;
    }
  
    setSaving(true);
    setError("");
  
    try {
      const payload = {
        CampusCode: loginCampusCode,
        SubCampusCode: projectForm.SubCampusCode || null,
        PromptTitle: promptForm.PromptTitle,
        PromptText: promptForm.PromptText,
        PromptPurpose: promptForm.PromptPurpose,
        CreatedBy: loginUsername,
        UpdatedBy: loginUsername,
      };
  
      if (promptForm.PromptID) {
        await axios.put(
          `${API_URL}/api/academic-projects/academic-project-prompts/${promptForm.PromptID}`,
          payload,
          { headers: authHeaders }
        );
  
        setSuccess("Prompt updated successfully");
      } else {
        await axios.post(
          `${API_URL}/api/academic-projects/academic-project-prompts`,
          payload,
          { headers: authHeaders }
        );
  
        setSuccess("Prompt saved successfully");
      }
  
      resetPromptForm();
      fetchPrompts();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save prompt");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPrompt = (row) => {
    setPromptForm({
      PromptID: row.PromptID || "",
      PromptTitle: row.PromptTitle || "",
      PromptPurpose: row.PromptPurpose || "PROJECT_IDEATION",
      PromptText: row.PromptText || "",
    });
  };

  const handleDeletePrompt = async (PromptID) => {
    if (!window.confirm("Delete this prompt?")) return;
  
    setSaving(true);
    setError("");
  
    try {
      await axios.delete(
        `${API_URL}/api/academic-projects/academic-project-prompts/${PromptID}`,
        { headers: authHeaders }
      );
  
      setSuccess("Prompt deleted successfully");
      fetchPrompts();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete prompt");
    } finally {
      setSaving(false);
    }
  };

  const validateProjectScope = () => {
    if (!projectForm.AcademicYear) return "Academic Year is required";
    if (!projectForm.SubCampusCode) return "SubCampus is required";
    if (!projectForm.DegreeCode) return "Degree is required";
    if (!projectForm.BranchCode) return "Branch is required";
    if (!projectForm.Regulation) return "Regulation is required";
    if (!projectForm.Semester) return "Semester is required";
    if (!projectForm.Sections.length) return "Select at least one section";
    if (!projectForm.Subjects.length) return "Select at least one subject";
    if (!normalizeAssignmentGroupCode(projectForm.AssignmentGroupCode)) {
      return "Assignment Group is required";
    }
    return "";
  };

  const validateProject = () => {
    const scopeError = validateProjectScope();
    if (scopeError) return scopeError;
    if (!projectForm.AssignmentGroupCode) return "Assignment Group is required";
    if (!projectForm.ProjectTitle) return "Project Title is required";
    if (!projectForm.Domain) return "Domain is required";
    if (!projectForm.ProjectDescription) return "Project Description is required";
    if (!projectForm.ProblemStatement) return "Problem Statement is required";
    if (!projectForm.ExpectedOutcome) return "Expected Outcome is required";
    if (!projectForm.TechStack) return "Tech Stack is required";
    if (!projectForm.StartDate) return "Start Date is required";
    if (!projectForm.EndDate) return "End Date is required";
    if (projectForm.EndDate < projectForm.StartDate) return "End Date cannot be before Start Date";
    if (!projectForm.MaxStudentsAllowed) return "Max Students Allowed is required";
    if (!projectForm.Subjects || projectForm.Subjects.length === 0) return "Subject is required";
    return "";
  };

  const handleGenerateAiProject = async () => {
    const scopeError = validateProjectScope();

    if (scopeError) {
      setError(scopeError);
      return;
    }

    if (!projectForm.PromptUsed || !projectForm.PromptUsed.trim()) {
      setError("Enter or use a prompt first in Prompt Used field");
      return;
    }

    const count = Number(projectVariationCount);

    if (!Number.isInteger(count) || count < 1 || count > 30) {
      setError("Number of project variations must be between 1 and 30");
      return;
    }

    setGeneratingProject(true);
    setError("");

    try {
      const res = await axios.post(
        `${API_URL}/api/academic-projects/generate-ai-project`,
        {
          CampusCode: loginCampusCode,
          SubCampusCode: projectForm.SubCampusCode,
          AcademicYear: projectForm.AcademicYear,
          DegreeCode: projectForm.DegreeCode,
          BranchCode: projectForm.BranchCode,
          Regulation: projectForm.Regulation,
          Semester: projectForm.Semester,
          Sections: projectForm.Sections,
          Subjects: projectForm.Subjects,
          CompletedTopics: completedTopics,
          PromptUsed: projectForm.PromptUsed,
          ProjectCount: count,
          MaxStudentsAllowed: Number(projectForm.MaxStudentsAllowed || 5),
        },
        { headers: authHeaders }
      );

      const aiProjects = Array.isArray(res.data?.projects)
        ? res.data.projects
        : [];

      if (!aiProjects.length) {
        throw new Error("AI did not return any project variations");
      }

      const firstPreviewNumber =
        Number(selectedAssignmentGroupInfo?.NextProjectNumber || 1) +
        generatedProjects.length;

      const batchMaxStudentsAllowed = Number(projectForm.MaxStudentsAllowed || 5);

      const newProjects = aiProjects.map((project, index) => ({
        ...project,
        ProjectNumber: firstPreviewNumber + index,
        MaxStudentsAllowed: batchMaxStudentsAllowed,
      }));

      setGeneratedProjects((prev) => [...prev, ...newProjects]);

      if (newProjects.length === 1) {
        const aiProject = newProjects[0];

        setProjectForm((prev) => ({
          ...prev,
          ProjectNumber: aiProject.ProjectNumber,
          ProjectTitle: aiProject.ProjectTitle || prev.ProjectTitle,
          Domain: aiProject.Domain || prev.Domain,
          ProjectDescription:
            aiProject.ProjectDescription || prev.ProjectDescription,
          ProblemStatement:
            aiProject.ProblemStatement || prev.ProblemStatement,
          ExpectedOutcome:
            aiProject.ExpectedOutcome || prev.ExpectedOutcome,
          TechStack: aiProject.TechStack || prev.TechStack,
          DifficultyLevel:
            aiProject.DifficultyLevel || prev.DifficultyLevel,
          MaxStudentsAllowed: prev.MaxStudentsAllowed,
        }));
      }

      setSuccess(
        `${newProjects.length} different project variation(s) generated. Review them below.`
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to generate AI project variations"
      );
    } finally {
      setGeneratingProject(false);
    }
  };

  const handleUseGeneratedProject = (project) => {
    setProjectForm((prev) => ({
      ...prev,
      ProjectNumber: project.ProjectNumber || "",
      ProjectTitle: project.ProjectTitle || "",
      Domain: project.Domain || "",
      ProjectDescription: project.ProjectDescription || "",
      ProblemStatement: project.ProblemStatement || "",
      ExpectedOutcome: project.ExpectedOutcome || "",
      TechStack: project.TechStack || "",
      DifficultyLevel: project.DifficultyLevel || "Medium",
      MaxStudentsAllowed: Number(projectForm.MaxStudentsAllowed || 5),
    }));

    setSuccess(`Project #${project.ProjectNumber || "-"} loaded into the editor`);
  };

  const handleRemoveGeneratedProject = (indexToRemove) => {
    const firstNumber = Number(
      selectedAssignmentGroupInfo?.NextProjectNumber || 1
    );

    setGeneratedProjects((prev) =>
      prev
        .filter((_, index) => index !== indexToRemove)
        .map((project, index) => ({
          ...project,
          ProjectNumber: firstNumber + index,
        }))
    );
  };

  const handleSaveGeneratedProjects = async (status = "DRAFT") => {
    if (!generatedProjects.length) {
      setError("Generate at least one project first");
      return;
    }

    const scopeError = validateProjectScope();

    if (scopeError) {
      setError(scopeError);
      return;
    }

    const safeStatus = status === "PUBLISHED" ? "PUBLISHED" : "DRAFT";
    const actionText =
      safeStatus === "PUBLISHED" ? "publish" : "save as draft";

    if (
      !window.confirm(
        `${actionText} all ${generatedProjects.length} generated project(s) in ${normalizeAssignmentGroupCode(
          projectForm.AssignmentGroupCode
        )}?`
      )
    ) {
      return;
    }

    setSavingGeneratedProjects(true);
    setError("");

    try {
      const res = await axios.post(
        `${API_URL}/api/academic-projects/save-generated-projects`,
        {
          SubCampusCode: projectForm.SubCampusCode,
          AcademicYear: projectForm.AcademicYear,
          DegreeCode: projectForm.DegreeCode,
          BranchCode: projectForm.BranchCode,
          Regulation: projectForm.Regulation,
          Semester: projectForm.Semester,
          Sections: projectForm.Sections,
          Subjects: projectForm.Subjects,
          AssignmentGroupCode: normalizeAssignmentGroupCode(
            projectForm.AssignmentGroupCode
          ),
          Projects: generatedProjects,
          MaxStudentsAllowed: Number(projectForm.MaxStudentsAllowed || 5),
          PromptUsed: projectForm.PromptUsed,
          StartDate: toDateInputValue(projectForm.StartDate),
          EndDate: toDateInputValue(projectForm.EndDate),
          CreatedByHOD: loginUsername,
          Status: safeStatus,
        },
        { headers: authHeaders }
      );

      setSuccess(
        res.data?.message ||
          `${generatedProjects.length} project(s) saved successfully`
      );

      setGeneratedProjects([]);

      await fetchProjects();
      await fetchAssignmentGroups();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to save generated projects"
      );
    } finally {
      setSavingGeneratedProjects(false);
    }
  };

  const handleBulkProjectClick = () => {
    bulkProjectFileRef.current?.click();
  };
  
  const handleBulkProjectUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
  
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", loginCampusCode);
  
    setSaving(true);
    setError("");
  
    try {
      const res = await axios.post(
        `${API_URL}/api/academic-projects/bulk-upload-projects`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
  
      setSuccess(res.data?.message || "Academic projects uploaded successfully");
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Bulk upload failed");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  const handleSaveProject = async (nextStatus = projectForm.Status) => {
    const validation = validateProject();
  
    if (validation) {
      setError(validation);
      return;
    }
  
    setSaving(true);
    setError("");
  
    try {
      const normalizedGroup = normalizeAssignmentGroupCode(
        projectForm.AssignmentGroupCode
      );

      const existingGroup = assignmentGroupOptions.find(
        (item) =>
          normalizeAssignmentGroupCode(item.AssignmentGroupCode) === normalizedGroup
      );

      const resolvedProjectNumber = projectForm.ProjectID
        ? projectForm.ProjectNumber
        : Number(
            projectForm.ProjectNumber ||
              existingGroup?.NextProjectNumber ||
              1
          );

      const payload = {
        CampusCode: loginCampusCode,
        ...projectForm,
        AssignmentGroupCode: normalizedGroup,
        ProjectNumber: resolvedProjectNumber,
        Status: nextStatus,
        StartDate: toDateInputValue(projectForm.StartDate),
        EndDate: toDateInputValue(projectForm.EndDate),
        MaxStudentsAllowed: Number(projectForm.MaxStudentsAllowed || 5),
        CreatedByHOD: loginUsername,
      };
  
      if (projectForm.ProjectID) {
        await axios.put(
          `${API_URL}/api/academic-projects/saveproject/${projectForm.ProjectID}`,
          {
            ...payload,
            UsedBy: loginUsername,
          },
          { headers: authHeaders }
        );
  
        setSuccess(nextStatus === "PUBLISHED" ? "Project published successfully" : "Project updated successfully");
      } else {
        await axios.post(`${API_URL}/api/academic-projects/saveproject`, payload, {
          headers: authHeaders,
        });
  
        setSuccess(nextStatus === "PUBLISHED" ? "Project published successfully" : "Project saved as draft");
      }
  
      resetProjectForm();
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const handleEditProject = (row) => {
    if (row.Status === "PUBLISHED") {
      setError("Published projects cannot be edited. Use Clone to create a new draft.");
      return;
    }
  
    setProjectForm({
      ProjectID: row.ProjectID || "",
      SubCampusCode: row.SubCampusCode || "",
      AcademicYear: row.AcademicYear || "",
      DegreeCode: row.DegreeCode || "",
      BranchCode: row.BranchCode || "",
      Regulation: row.Regulation || "",
      Semester: row.Semester || "",
      Sections: row.Sections || [],
      Subjects: row.Subjects || [],
      AssignmentGroupCode: row.AssignmentGroupCode || "",
      ProjectNumber: row.ProjectNumber || "",
      ProjectTitle: row.ProjectTitle || "",
      Domain: row.Domain || "",
      ProjectDescription: row.ProjectDescription || "",
      ProblemStatement: row.ProblemStatement || "",
      ExpectedOutcome: row.ExpectedOutcome || "",
      PromptUsed: row.PromptUsed || "",
      TechStack: row.TechStack || "",
      DifficultyLevel: row.DifficultyLevel || "Medium",
      StartDate: row.StartDate ? String(row.StartDate).slice(0, 10) : "",
      EndDate: row.EndDate ? String(row.EndDate).slice(0, 10) : "",
      MaxStudentsAllowed: row.MaxStudentsAllowed || 5,
      Status: row.Status || "DRAFT",
    });
  
    setTab(0);
  };

  const handleDeleteProject = async (ProjectID) => {
    if (!window.confirm("Delete this project?")) return;

    setSaving(true);
    setError("");

    try {
      await axios.delete(`${API_URL}/api/academic-projects/${ProjectID}`, {
        headers: authHeaders,
      });

      setSuccess("Project deleted successfully");
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete project");
    } finally {
      setSaving(false);
    }
  };

  const pagedProjects = useMemo(() => {
    const start = (projectPage - 1) * PROJECTS_PER_PAGE;
    return projects.slice(start, start + PROJECTS_PER_PAGE);
  }, [projects, projectPage]);

  const projectPageCount = Math.max(1, Math.ceil(projects.length / PROJECTS_PER_PAGE));

  const uniqueValues = (key) =>
    Array.from(
      new Set(
        projects
          .map((x) => x[key])
          .filter((v) => String(v || "").trim() !== "")
      )
    ).sort();
  
  const filterOptions = {
    AcademicYear: academicYearOptions,
    SubCampusCode: uniqueValues("SubCampusCode"),
    DegreeCode: uniqueValues("DegreeCode"),
    BranchCode: uniqueValues("BranchCode"),
    Regulation: uniqueValues("Regulation"),
    Semester: uniqueValues("Semester"),
    AssignmentGroupCode: uniqueValues("AssignmentGroupCode"),
    Status: ["DRAFT", "PUBLISHED"],
  };

  const handleCreateFromExisting = async (row) => {
    const normalizedGroup = normalizeAssignmentGroupCode(
      row.AssignmentGroupCode || ""
    );

    let suggestedProjectNumber = 1;

    try {
      if (
        normalizedGroup &&
        row.SubCampusCode &&
        row.AcademicYear &&
        row.DegreeCode &&
        row.BranchCode &&
        row.Regulation &&
        row.Semester
      ) {
        const res = await axios.get(
          `${API_URL}/api/academic-projects/assignment-groups`,
          {
            headers: authHeaders,
            params: {
              SubCampusCode: row.SubCampusCode,
              AcademicYear: row.AcademicYear,
              DegreeCode: row.DegreeCode,
              BranchCode: row.BranchCode,
              Regulation: row.Regulation,
              Semester: row.Semester,
            },
          }
        );

        const groups = Array.isArray(res.data) ? res.data : [];
        const existingGroup = groups.find(
          (item) =>
            normalizeAssignmentGroupCode(item.AssignmentGroupCode) ===
            normalizedGroup
        );

        suggestedProjectNumber = Number(
          existingGroup?.NextProjectNumber || 1
        );
      }
    } catch (err) {
      // Fall back to the best locally available suggestion if the lookup fails.
      const localGroup = assignmentGroupOptions.find(
        (item) =>
          normalizeAssignmentGroupCode(item.AssignmentGroupCode) ===
          normalizedGroup
      );

      suggestedProjectNumber = Number(
        localGroup?.NextProjectNumber || 1
      );
    }

    setProjectForm({
      ...emptyProjectForm,

      SubCampusCode: row.SubCampusCode || "",
      AcademicYear: row.AcademicYear || "",
      DegreeCode: row.DegreeCode || "",
      BranchCode: row.BranchCode || "",
      Regulation: row.Regulation || "",
      Semester: row.Semester || "",

      AssignmentGroupCode: normalizedGroup,
      ProjectNumber: suggestedProjectNumber,
      ProjectTitle: row.ProjectTitle ? `${row.ProjectTitle} - Copy` : "",
      Domain: row.Domain || "",
      ProjectDescription: row.ProjectDescription || "",
      ProblemStatement: row.ProblemStatement || "",
      ExpectedOutcome: row.ExpectedOutcome || "",
      PromptUsed: row.PromptUsed || "",

      TechStack: row.TechStack || "",
      DifficultyLevel: row.DifficultyLevel || "Medium",
      StartDate: "",
      EndDate: "",
      MaxStudentsAllowed: row.MaxStudentsAllowed || 5,
      Status: "DRAFT",

      Sections: [],
      Subjects: [],
    });

    setCompletedTopics([]);
    setSectionTeacherOptions([]);
    setTab(0);
    setSuccess(
      `Existing project copied. Project number ${suggestedProjectNumber} has been selected for ${normalizedGroup || "the new batch"}. Select sections/subjects and save as a new project.`
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Academic Projects - HOD
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Create project topics, maintain prompt bank, and assign projects to class teachers
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">

            <Button
              variant="outlined"
              sx={{
                ...outlinedBtnSx,
                color: "white",
                borderColor: "rgba(255,255,255,0.55)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.12)",
                },
              }}
              onClick={downloadProjectSampleExcel}
            >
              Download Sample
            </Button>
          
            <Button
              variant="contained"
              sx={{
                ...primaryBtnSx,
                backgroundColor: "white",
                color: THEME_ORANGE,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
              }}
              startIcon={<UploadIcon />}
              onClick={handleBulkProjectClick}
              disabled={saving}
            >
              Bulk Upload
            </Button>
          
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={bulkProjectFileRef}
              style={{ display: "none" }}
              onChange={handleBulkProjectUpload}
            />

            <Button
              variant="outlined"
              sx={{
                ...outlinedBtnSx,
                color: "white",
                borderColor: "rgba(255,255,255,0.55)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.12)",
                },
              }}
              startIcon={<RefreshIcon />}
              onClick={fetchProjects}
              disabled={loading}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              sx={{
                ...primaryBtnSx,
                backgroundColor: "white",
                color: THEME_ORANGE,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
              }}
              startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
              //onClick={handleSaveProject}
              onClick={() => handleSaveProject("DRAFT")}
              disabled={saving}
            >
              {projectForm.ProjectID ? "Update Draft" : "Save Draft"}
            </Button>
          </Stack>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={2500} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                px: 1,
                borderBottom: `1px solid ${BORDER}`,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
                "& .Mui-selected": { color: THEME_ORANGE },
                "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
              }}
            >
              <Tab label="Create Project" icon={<FolderSpecialIcon />} iconPosition="start" />
              <Tab label="Prompt Bank" icon={<AutoAwesomeIcon />} iconPosition="start" />
              <Tab label="Existing Projects" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="Create From Existing" icon={<ContentCopyIcon />} iconPosition="start" />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Typography sx={sectionTitleSx}>Project Scope</Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Academic Year"
                            name="AcademicYear"
                            value={projectForm.AcademicYear}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          >
                            {academicYearOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={9}>
                          <TextField
                            select
                            label="SubCampus"
                            name="SubCampusCode"
                            value={projectForm.SubCampusCode}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          >
                            {subCampusOptions.map((opt) => (
                              <MenuItem key={opt.SubCampusCode} value={opt.SubCampusCode}>
                                {opt.SubCampusCode} {opt.CampusName ? `- ${opt.CampusName}` : ""}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Degree"
                            name="DegreeCode"
                            value={projectForm.DegreeCode}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            disabled={!projectForm.SubCampusCode}
                          >
                            {degreeOptions.map((opt) => (
                              <MenuItem key={opt.DegreeCode} value={opt.DegreeCode}>
                                {opt.DegreeCode}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Branch"
                            name="BranchCode"
                            value={projectForm.BranchCode}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            disabled={!projectForm.DegreeCode}
                          >
                            {branchOptions.map((opt) => (
                              <MenuItem key={opt.BranchCode} value={opt.BranchCode}>
                                {opt.BranchCode}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Regulation"
                            name="Regulation"
                            value={projectForm.Regulation}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            disabled={!projectForm.BranchCode}
                          >
                            {regulationOptions.map((opt) => (
                              <MenuItem key={opt.Regulation} value={opt.Regulation}>
                                {opt.Regulation}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Semester"
                            name="Semester"
                            value={projectForm.Semester}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            disabled={!projectForm.BranchCode}
                          >
                            {semesterOptions.map((opt) => (
                              <MenuItem key={opt.Semester} value={opt.Semester}>
                                {opt.Semester}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6}>
                          <Autocomplete
                            multiple
                            options={sectionOptions}
                            getOptionLabel={(option) => option.Section || ""}
                            value={sectionOptions.filter((x) =>
                              projectForm.Sections.includes(x.Section)
                            )}
                            onChange={(_, value) => {
                              setGeneratedProjects([]);
                              setProjectForm((prev) => ({
                                ...prev,
                                Sections: value.map((x) => x.Section),
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Sections"
                                size="small"
                                sx={inputSx}
                                helperText="Select one or more sections. Class teacher will see this project only after selecting matching section."
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <Autocomplete
                            multiple
                            options={subjectOptions}
                            getOptionLabel={(option) =>
                              `${option.SubjectCode} - ${option.SubjectName || option.SubjectNameCode || ""} - Section ${option.Section}`
                            }
                            value={projectForm.Subjects}
                            onChange={(_, value) => {
                              setGeneratedProjects([]);
                              setProjectForm((prev) => ({
                                ...prev,
                                Subjects: value,
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Related Subjects"
                                size="small"
                                sx={inputSx}
                                helperText="Select one or more subjects. Completed topics will appear on the right side."
                              />
                            )}
                          />
                        </Grid>


                      </Grid>
                    </Paper>

                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mt: 2 }}>
                      <Typography sx={sectionTitleSx}>Project Details</Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            freeSolo
                            options={assignmentGroupOptions.map(
                              (item) => item.AssignmentGroupCode
                            )}
                            value={projectForm.AssignmentGroupCode || ""}
                            onChange={(_, newValue) => {
                              setGeneratedProjects([]);

                              const normalizedGroup =
                                normalizeAssignmentGroupCode(newValue || "");

                              const existingGroup = assignmentGroupOptions.find(
                                (item) =>
                                  normalizeAssignmentGroupCode(item.AssignmentGroupCode) ===
                                  normalizedGroup
                              );

                              setProjectForm((prev) => ({
                                ...prev,
                                AssignmentGroupCode: normalizedGroup,
                                ProjectNumber: normalizedGroup
                                  ? Number(existingGroup?.NextProjectNumber || 1)
                                  : "",
                              }));
                            }}
                            onInputChange={(_, newInputValue, reason) => {
                              if (reason === "input") {
                                setGeneratedProjects([]);

                                const normalizedGroup =
                                  normalizeAssignmentGroupCode(newInputValue || "");

                                const existingGroup = assignmentGroupOptions.find(
                                  (item) =>
                                    normalizeAssignmentGroupCode(item.AssignmentGroupCode) ===
                                    normalizedGroup
                                );

                                setProjectForm((prev) => ({
                                  ...prev,
                                  AssignmentGroupCode: newInputValue,
                                  ProjectNumber: normalizedGroup
                                    ? Number(existingGroup?.NextProjectNumber || 1)
                                    : "",
                                }));
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Assignment Group"
                                size="small"
                                sx={inputSx}
                                placeholder="Select an existing group or type a new one"
                                helperText={
                                  selectedAssignmentGroupInfo
                                    ? `${selectedAssignmentGroupInfo.ProjectCount} existing project(s). Next project number: ${selectedAssignmentGroupInfo.NextProjectNumber}.`
                                    : "Choose an existing group from the dropdown, or type a new group such as SubjectCode_ProjectNumber."
                                }
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Project Variations"
                            type="number"
                            value={projectVariationCount}
                            onChange={(e) =>
                              setProjectVariationCount(
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value)
                              )
                            }
                            fullWidth
                            size="small"
                            sx={inputSx}
                            inputProps={{ min: 1, max: 30 }}
                            helperText="How many AI projects? 1-30."
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Project Number"
                            name="ProjectNumber"
                            type="number"
                            value={projectForm.ProjectNumber}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            inputProps={{ min: 1 }}
                            helperText={`For single/manual save. Suggested next: ${
                              selectedAssignmentGroupInfo?.NextProjectNumber || 1
                            }`}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Project Title"
                            name="ProjectTitle"
                            value={projectForm.ProjectTitle}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={12}>
                          <TextField
                            label="Domain"
                            name="Domain"
                            value={projectForm.Domain}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            placeholder="Agriculture, Robotics, Healthcare, IoT..."
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Project Description"
                            name="ProjectDescription"
                            value={projectForm.ProjectDescription}
                            onChange={handleProjectChange}
                            fullWidth
                            multiline
                            minRows={2}
                            sx={inputSx}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Problem Statement"
                            name="ProblemStatement"
                            value={projectForm.ProblemStatement}
                            onChange={handleProjectChange}
                            fullWidth
                            multiline
                            minRows={3}
                            sx={inputSx}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Expected Outcome"
                            name="ExpectedOutcome"
                            value={projectForm.ExpectedOutcome}
                            onChange={handleProjectChange}
                            fullWidth
                            multiline
                            minRows={2}
                            sx={inputSx}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Prompt Used"
                            name="PromptUsed"
                            value={projectForm.PromptUsed}
                            onChange={handleProjectChange}
                            fullWidth
                            multiline
                            minRows={4}
                            sx={inputSx}
                            placeholder="Track the prompt used to generate/refine this project topic."
                          />
                        </Grid>

                        <Grid item xs={12} sm={9}>
                          <TextField
                            label="Tech Stack"
                            name="TechStack"
                            value={projectForm.TechStack}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            placeholder="React, Node.js, MySQL, Python..."
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            label="Difficulty"
                            name="DifficultyLevel"
                            value={projectForm.DifficultyLevel}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          >
                            <MenuItem value="Easy">Easy</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="Hard">Hard</MenuItem>
                            <MenuItem value="Research">Research</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={4.5}>
                          <DatePicker
                            label="Start Date"
                            format="YYYY-MM-DD"
                            value={projectForm.StartDate ? dayjs(projectForm.StartDate) : null}
                            onChange={(newValue) => {
                              setProjectForm((prev) => ({
                                ...prev,
                                StartDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                              }));
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                size: "small",
                                sx: inputSx,
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <CalendarMonthIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                },
                              },
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={4.5}>
                          <DatePicker
                            label="End Date"
                            format="YYYY-MM-DD"
                            minDate={projectForm.StartDate ? dayjs(projectForm.StartDate) : undefined}
                            value={projectForm.EndDate ? dayjs(projectForm.EndDate) : null}
                            onChange={(newValue) => {
                              setProjectForm((prev) => ({
                                ...prev,
                                EndDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                              }));
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                size: "small",
                                sx: inputSx,
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <CalendarMonthIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Students Allowed"
                            name="MaxStudentsAllowed"
                            type="number"
                            value={projectForm.MaxStudentsAllowed}
                            onChange={handleProjectChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            inputProps={{ min: 1 }}
                          />
                        </Grid>

                      </Grid>

                     <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                       <Button
                          variant="contained"
                          sx={{
                            ...primaryBtnSx,
                            backgroundColor: BLUE,
                            "&:hover": { backgroundColor: "#0D47A1" },
                          }}
                          startIcon={
                            generatingProject ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <AutoAwesomeIcon />
                            )
                          }
                          onClick={handleGenerateAiProject}
                          disabled={generatingProject || saving}
                        >
                          {generatingProject
                            ? `Generating ${projectVariationCount || ""} Projects...`
                            : `Generate ${projectVariationCount || ""} Project Variations`}
                        </Button>

                       <Button
                         variant="contained"
                         sx={primaryBtnSx}
                         startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                         onClick={() => handleSaveProject("DRAFT")}
                         disabled={saving}
                       >
                         {projectForm.ProjectID ? "Update Draft" : "Save Draft"}
                       </Button>
                                             
                       <Button
                         variant="contained"
                         sx={primaryBtnSx}
                         onClick={() => handleSaveProject("PUBLISHED")}
                         disabled={saving}
                       >
                         Publish
                       </Button>
                                             
                       <Button
                         variant="outlined"
                         sx={outlinedBtnSx}
                         startIcon={<AddIcon />}
                         onClick={resetProjectForm}
                       >
                         New
                       </Button>
                     </Stack>

                     {!!generatedProjects.length && (
                       <Paper
                         elevation={0}
                         sx={{
                           mt: 2,
                           p: 2,
                           borderRadius: 2,
                           border: `1px solid ${BORDER}`,
                           backgroundColor: "rgba(25,118,210,0.03)",
                         }}
                       >
                         <Stack
                           direction={{ xs: "column", md: "row" }}
                           justifyContent="space-between"
                           spacing={2}
                           sx={{ mb: 2 }}
                         >
                           <Box>
                             <Typography sx={{ fontWeight: 900, color: BLUE }}>
                               Generated Project Variations
                             </Typography>
                             <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                               {generatedProjects.length} unsaved project(s) for{" "}
                               {normalizeAssignmentGroupCode(projectForm.AssignmentGroupCode)}.
                               Project numbers shown below are previews; the backend assigns the final
                               sequential numbers when you save.
                             </Typography>
                           </Box>

                           <Stack direction="row" spacing={1} flexWrap="wrap">
                             <Button
                               variant="contained"
                               sx={primaryBtnSx}
                               startIcon={
                                 savingGeneratedProjects ? (
                                   <CircularProgress size={18} color="inherit" />
                                 ) : (
                                   <SaveIcon />
                                 )
                               }
                               onClick={() => handleSaveGeneratedProjects("DRAFT")}
                               disabled={savingGeneratedProjects}
                             >
                               Save All as Draft
                             </Button>

                             <Button
                               variant="contained"
                               sx={{
                                 ...primaryBtnSx,
                                 backgroundColor: BLUE,
                                 "&:hover": { backgroundColor: "#0D47A1" },
                               }}
                               onClick={() => handleSaveGeneratedProjects("PUBLISHED")}
                               disabled={savingGeneratedProjects}
                             >
                               Publish All
                             </Button>
                           </Stack>
                         </Stack>

                         <TableContainer
                           component={Paper}
                           elevation={0}
                           sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}
                         >
                           <Table size="small">
                             <TableHead>
                               <TableRow sx={theadRowSx}>
                                 <TableCell>#</TableCell>
                                 <TableCell>Project</TableCell>
                                 <TableCell>Domain</TableCell>
                                 <TableCell>Difficulty</TableCell>
                                 <TableCell>Tech Stack</TableCell>
                                 <TableCell>Actions</TableCell>
                               </TableRow>
                             </TableHead>

                             <TableBody>
                               {generatedProjects.map((project, index) => (
                                 <TableRow
                                   key={`${project.ProjectTitle}-${index}`}
                                   sx={zebraRowSx(index)}
                                 >
                                   <TableCell>{project.ProjectNumber}</TableCell>
                                   <TableCell>
                                     <Typography sx={{ fontWeight: 900 }}>
                                       {project.ProjectTitle}
                                     </Typography>
                                     <Typography
                                       sx={{
                                         color: "text.secondary",
                                         fontSize: "0.8rem",
                                         maxWidth: 420,
                                       }}
                                     >
                                       {project.ProblemStatement || "-"}
                                     </Typography>
                                   </TableCell>
                                   <TableCell>{project.Domain || "-"}</TableCell>
                                   <TableCell>
                                     <Chip
                                       size="small"
                                       label={project.DifficultyLevel || "Medium"}
                                     />
                                   </TableCell>
                                   <TableCell>{project.TechStack || "-"}</TableCell>
                                   <TableCell>
                                     <Stack direction="row" spacing={1}>
                                       <Button
                                         size="small"
                                         variant="outlined"
                                         sx={outlinedBtnSx}
                                         startIcon={<EditIcon />}
                                         onClick={() => handleUseGeneratedProject(project)}
                                       >
                                         Edit
                                       </Button>

                                       <Button
                                         size="small"
                                         variant="outlined"
                                         color="error"
                                         startIcon={<DeleteIcon />}
                                         onClick={() =>
                                           handleRemoveGeneratedProject(index)
                                         }
                                       >
                                         Remove
                                       </Button>
                                     </Stack>
                                   </TableCell>
                                 </TableRow>
                               ))}
                             </TableBody>
                           </Table>
                         </TableContainer>
                       </Paper>
                     )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={5}>

                 
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${BORDER}`,
                          backgroundColor: "rgba(25,118,210,0.03)",
                        }}
                      >
                        <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                          Class Teachers for Selected Sections
                        </Typography>
                    
                        {!projectForm.Sections.length ? (
                          <Typography sx={{ color: "text.secondary" }}>
                            Select sections to view class teachers.
                          </Typography>
                        ) : !sectionTeacherOptions.length ? (
                          <Typography sx={{ color: "text.secondary" }}>
                            No class teachers found for selected sections.
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {sectionTeacherOptions.map((teacher, idx) => (
                              <Chip
                                key={`${teacher.Section}-${teacher.Username}-${idx}`}
                                label={`Section ${teacher.Section}: ${teacher.TeacherName || teacher.Username || "-"}`}
                                sx={{
                                  fontWeight: 800,
                                  color: BLUE,
                                  backgroundColor: "rgba(25,118,210,0.08)",
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Paper>
                  
                    <Divider sx={{ my: 1 }} />

                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Typography sx={sectionTitleSx}>Completed Topics from Selected Subjects</Typography>

                      {!projectForm.Subjects.length ? (
                        <Typography sx={{ color: "text.secondary" }}>
                          Select one or more subjects to view completed topics.
                        </Typography>
                      ) : !completedTopics.length ? (
                        <Typography sx={{ color: "text.secondary" }}>
                          No completed topics found for selected subjects.
                        </Typography>
                      ) : (
                        <Stack spacing={1.25}>
                          {completedTopics.map((topic) => (
                            <Paper
                              key={topic.SessionID}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: `1px solid ${BORDER}`,
                                backgroundColor: "rgba(25,118,210,0.03)",
                              }}
                            >
                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 0.75 }}>
                                <Chip size="small" label={topic.SubjectCode} />
                                <Chip size="small" label={`Section ${topic.Section}`} />
                                <Chip size="small" label={String(topic.AttendanceDate).slice(0, 10)} />
                              </Stack>
                          
                              <Typography sx={{ fontWeight: 700 }}>
                                {topic.TopicsCovered}
                              </Typography>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Typography sx={sectionTitleSx}>Add Prompt</Typography>

                      <Stack spacing={2}>
                        <TextField
                          label="Prompt Title"
                          name="PromptTitle"
                          value={promptForm.PromptTitle}
                          onChange={handlePromptChange}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />

                        <TextField
                          select
                          label="Prompt Purpose"
                          name="PromptPurpose"
                          value={promptForm.PromptPurpose}
                          onChange={handlePromptChange}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        >
                          <MenuItem value="PROJECT_IDEATION">Project Ideation</MenuItem>
                          <MenuItem value="PROJECT_REFINEMENT">Project Refinement</MenuItem>
                          <MenuItem value="RUBRIC_GENERATION">Rubric Generation</MenuItem>
                          <MenuItem value="TIMELINE_GENERATION">Timeline Generation</MenuItem>
                        </TextField>

                        <TextField
                          label="Prompt Text"
                          name="PromptText"
                          value={promptForm.PromptText}
                          onChange={handlePromptChange}
                          fullWidth
                          multiline
                          minRows={8}
                          sx={inputSx}
                        />

                        <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              sx={primaryBtnSx}
                              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                              onClick={handleSavePrompt}
                              disabled={saving}
                            >
                              {promptForm.PromptID ? "Update Prompt" : "Save Prompt"}
                            </Button>

                          <Button variant="outlined" sx={outlinedBtnSx} onClick={resetPromptForm}>
                            Clear
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={tableWrapSx}>
                      <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component="div">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={theadRowSx}>
                                <TableCell>Title</TableCell>
                                <TableCell>Purpose</TableCell>
                                <TableCell>Prompt</TableCell>
                                <TableCell>Action</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {prompts.map((row, idx) => (
                                <TableRow key={row.PromptID} sx={zebraRowSx(idx)}>
                                  <TableCell>{row.PromptTitle}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={row.PromptPurpose || "-"} />
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        maxWidth: 420,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {row.PromptText}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={outlinedBtnSx}
                                        onClick={() => handleUsePrompt(row)}
                                      >
                                        Use
                                      </Button>

                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={outlinedBtnSx}
                                        startIcon={<ContentCopyIcon />}
                                        onClick={() => handleCopyPrompt(row.PromptText)}
                                      >
                                        Copy
                                      </Button>

                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={outlinedBtnSx}
                                        startIcon={<EditIcon />}
                                        onClick={() => handleEditPrompt(row)}
                                      >
                                        Edit
                                      </Button>

                                      <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleDeletePrompt(row.PromptID)}
                                      >
                                        Delete
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}

                              {!prompts.length && (
                                <TableRow>
                                  <TableCell colSpan={4}>
                                    <Typography sx={{ color: "text.secondary", p: 2 }}>
                                      No prompts found.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ p: 2 }}>
                <Paper elevation={0} sx={filterSectionSx}>
                  <Box sx={{ pl: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 2 }}>
                      Filters
                    </Typography>

                    <Grid container spacing={1.5}>
                      {[
                        ["AcademicYear", "Academic Year"],
                        ["SubCampusCode", "SubCampus"],
                        ["DegreeCode", "Degree"],
                        ["BranchCode", "Branch"],
                        ["Regulation", "Regulation"],
                        ["Semester", "Semester"],
                        ["AssignmentGroupCode", "Group"],
                        ["Status", "Status"],
                      ].map(([name, label]) => (
                        <Grid item xs={12} sm={6} md={1.7} key={name}>
                          <TextField
                            select
                            label={label}
                            name={name}
                            value={filters[name]}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          >
                            <MenuItem value="">All</MenuItem>
                            {(filterOptions[name] || []).map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      ))}

                      <Grid item xs={12} sm={6} md={2}>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            onClick={fetchProjects}
                            disabled={loading}
                          >
                            Filter
                          </Button>
                  
                          <Button
                            variant="outlined"
                            sx={outlinedBtnSx}
                            onClick={() =>
                              setFilters({
                                SubCampusCode: "",
                                AcademicYear: "",
                                DegreeCode: "",
                                BranchCode: "",
                                Regulation: "",
                                Semester: "",
                                Section: "",
                                AssignmentGroupCode: "",
                                Status: "",
                              })
                            }
                          >
                            Clear
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>

                <Paper elevation={0} sx={tableWrapSx}>
                  {loading ? (
                    <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography>Loading projects...</Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component="div">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={theadRowSx}>
                                <TableCell>#</TableCell>
                                <TableCell>Group</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Scope</TableCell>
                                <TableCell>Timeline</TableCell>
                                <TableCell>Difficulty</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {pagedProjects.map((row, idx) => (
                                <TableRow key={row.ProjectID} sx={zebraRowSx(idx)}>
                                  <TableCell>{(projectPage - 1) * PROJECTS_PER_PAGE + idx + 1}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={row.AssignmentGroupCode || "-"} />
                                    <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.5 }}>
                                      Project #{row.ProjectNumber || "-"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      {row.ProjectTitle}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "0.85rem",
                                        maxWidth: 360,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {row.ProblemStatement || "-"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography sx={{ fontSize: "0.85rem" }}>
                                      {row.AcademicYear} / {row.DegreeCode || "-"} / {row.BranchCode || "-"}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                                      Sem {row.Semester || "-"} / Sec {row.Section || "-"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {row.StartDate ? String(row.StartDate).slice(0, 10) : "-"} to{" "}
                                    {row.EndDate ? String(row.EndDate).slice(0, 10) : "-"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" label={row.DifficultyLevel || "-"} />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={row.Status || "-"}
                                      sx={{
                                        fontWeight: 800,
                                        color: BLUE,
                                        backgroundColor: "rgba(25,118,210,0.08)",
                                      }}
                                    />
                                  </TableCell>
                                  
                                  <TableCell>
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={outlinedBtnSx}
                                        startIcon={<ContentCopyIcon />}
                                        onClick={() => handleCreateFromExisting(row)}
                                      >
                                        Clone
                                      </Button>

                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={outlinedBtnSx}
                                        startIcon={<EditIcon />}
                                        onClick={() => handleEditProject(row)}
                                        disabled={row.Status === "PUBLISHED"}
                                      >
                                        Edit
                                      </Button>

                                      <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleDeleteProject(row.ProjectID)}
                                      >
                                        Delete
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}

                              {!projects.length && (
                                <TableRow>
                                  <TableCell colSpan={8}>
                                    <Typography sx={{ color: "text.secondary", p: 2 }}>
                                      No academic projects found.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          borderTop: "1px solid rgba(25,118,210,0.10)",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                          Showing {pagedProjects.length} of {projects.length} projects
                        </Typography>

                        <Pagination
                          count={projectPageCount}
                          page={projectPage}
                          onChange={(_, page) => setProjectPage(page)}
                          color="primary"
                          shape="rounded"
                        />
                      </Box>
                    </>
                  )}
                </Paper>
              </Box>
            )}

            {tab === 3 && (
              <Box sx={{ p: 2 }}>
                <Paper elevation={0} sx={filterSectionSx}>
                  <Box sx={{ pl: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                      Create Project From Existing
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.9rem", mb: 2 }}>
                      Select an existing project to copy its title, domain, description, problem statement,
                      expected outcome, prompt used, tech stack, difficulty, and student limit.
                      You will still select new sections and subjects before saving.
                    </Typography>

                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                      Filters
                    </Typography>

                    <Grid container spacing={1.5}>
                      {[
                        ["AcademicYear", "Academic Year"],
                        ["SubCampusCode", "SubCampus"],
                        ["DegreeCode", "Degree"],
                        ["BranchCode", "Branch"],
                        ["Regulation", "Regulation"],
                        ["Semester", "Semester"],
                        ["AssignmentGroupCode", "Group"],
                        ["Status", "Status"],
                      ].map(([name, label]) => (
                        <Grid item xs={12} sm={6} md={1.7} key={name}>
                          <TextField
                            select
                            label={label}
                            name={name}
                            value={filters[name]}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          >
                            <MenuItem value="">All</MenuItem>
                            {(filterOptions[name] || []).map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      ))}

                      <Grid item xs={12} sm={6} md={2}>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            onClick={fetchProjects}
                            disabled={loading}
                          >
                            Filter
                          </Button>

                          <Button
                            variant="outlined"
                            sx={outlinedBtnSx}
                            onClick={() => {
                              setFilters({
                                SubCampusCode: "",
                                AcademicYear: "",
                                DegreeCode: "",
                                BranchCode: "",
                                Regulation: "",
                                Semester: "",
                                Section: "",
                                AssignmentGroupCode: "",
                                Status: "",
                              });
                              setProjectPage(1);
                            }}
                          >
                            Clear
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>

                <Paper elevation={0} sx={tableWrapSx}>
                  <Box sx={{ overflowX: "auto" }}>
                    <TableContainer component="div">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            <TableCell>#</TableCell>
                            <TableCell>Group</TableCell>
                            <TableCell>Project</TableCell>
                            <TableCell>Domain</TableCell>
                            <TableCell>Scope</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {pagedProjects.map((row, idx) => (
                            <TableRow key={row.ProjectID} sx={zebraRowSx(idx)}>
                              <TableCell>{(projectPage - 1) * PROJECTS_PER_PAGE + idx + 1}</TableCell>
                              <TableCell>
                                <Chip size="small" label={row.AssignmentGroupCode || "-"} />
                              </TableCell>
                        
                              <TableCell>
                                <Typography sx={{ fontWeight: 900 }}>
                                  {row.ProjectTitle}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.85rem",
                                    maxWidth: 420,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {row.ProblemStatement || "-"}
                                </Typography>
                              </TableCell>
                              
                              <TableCell>{row.Domain || "-"}</TableCell>
                              
                              <TableCell>
                                {row.AcademicYear || "-"} / {row.DegreeCode || "-"} /{" "}
                                {row.BranchCode || "-"} / {row.Regulation || "-"} / Sem{" "}
                                {row.Semester || "-"}
                              </TableCell>
                              
                              <TableCell>
                                <Chip size="small" label={row.Status || "-"} />
                              </TableCell>
                              
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="contained"
                                  sx={primaryBtnSx}
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => handleCreateFromExisting(row)}
                                >
                                  Use This
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}

                          {!projects.length && (
                            <TableRow>
                              <TableCell colSpan={7}>
                                <Typography sx={{ color: "text.secondary", p: 2 }}>
                                  No existing projects found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      borderTop: "1px solid rgba(25,118,210,0.10)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                      Showing {pagedProjects.length} of {projects.length} projects
                    </Typography>

                    <Pagination
                      count={projectPageCount}
                      page={projectPage}
                      onChange={(_, page) => setProjectPage(page)}
                      color="primary"
                      shape="rounded"
                    />
                  </Box>
                </Paper>
              </Box>
            )}

          </Paper>
        </Box>
      </Paper>
    
    </Box>
    </LocalizationProvider>
  );
};

export default AcademicProjectHODManager;